import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getConversationActivity } from "../../actions/assistants";
import { CheckCircleIcon } from "./icons";

// GET .../conversations/:id/activity returns a chronological list: started,
// status changes, lead captured, tag edits. The exact field names couldn't be
// confirmed against a live session, so each is read through a few likely
// spellings and anything unrecognised still renders with its raw type as the
// heading rather than being dropped.

const firstOf = (source, keys) => {
  if (!source) return undefined;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return undefined;
};

// "status_changed" / "statusChanged" -> "Status changed"
const humanize = (value) =>
  String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase()) || "Activity";

const when = (value) => {
  if (!value) return "";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
};

// A change entry usually carries what it moved between; showing that is the
// whole point of a timeline, so it is pulled out rather than left in metadata.
const detailOf = (entry) => {
  const described = firstOf(entry, [
    "description",
    "message",
    "detail",
    "summary",
    "text",
  ]);
  if (described) return String(described);

  const from = firstOf(entry, ["from", "previous", "oldValue", "before"]);
  const to = firstOf(entry, ["to", "current", "newValue", "value", "after"]);

  if (from !== undefined && to !== undefined) return `${from} → ${to}`;
  if (to !== undefined) return String(to);

  return "";
};

const readEntry = (entry, index) => ({
  id: firstOf(entry, ["id", "_id"]) || `activity-${index}`,
  title: humanize(firstOf(entry, ["type", "event", "action", "kind", "title"])),
  detail: detailOf(entry),
  actor: firstOf(entry, ["actor", "by", "user", "author", "source"]),
  time: when(firstOf(entry, ["createdAt", "at", "timestamp", "time", "occurredAt"])),
});

export default function ActivityTab({ assistantId, conversationId }) {
  const dispatch = useDispatch();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!assistantId || !conversationId) {
      setEntries([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    dispatch(getConversationActivity(assistantId, conversationId)).then(
      (result) => {
        if (cancelled) return;

        setLoading(false);

        if (!result?.success) {
          setError(
            result?.message || "We couldn't load this conversation's activity.",
          );
          setEntries([]);
          return;
        }

        setEntries((result.activity || []).map(readEntry));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [assistantId, conversationId, dispatch]);

  if (loading) {
    return (
      <div className="conv-activity" aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div className="conv-activity-row" key={row}>
            <span className="conv-activity-marker" />
            <div className="conv-activity-body">
              <span className="skel acct-skel--label" />
              <span className="skel acct-skel--desc" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) return <p className="auth-error">{error}</p>;

  if (!entries.length) {
    return <p className="dash-empty">No activity recorded for this conversation.</p>;
  }

  return (
    <ol className="conv-activity">
      {entries.map((entry) => (
        <li className="conv-activity-row" key={entry.id}>
          <span className="conv-activity-marker">
            <CheckCircleIcon />
          </span>
          <div className="conv-activity-body">
            <strong>{entry.title}</strong>
            {entry.detail && (
              <span className="conv-activity-detail">{entry.detail}</span>
            )}
            <span className="conv-activity-meta">
              {[entry.actor, entry.time].filter(Boolean).join(" • ")}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}
